/**
 * Branching adapter that wires `@nextday/funnel-engine`'s sync
 * `ChooseNextStep` callback to `@nextday/rules`' async `evaluateRules`
 * (`json-rules-engine` per PLAN.md §6.1 / D-39).
 *
 * `funnel-engine`'s `advance()` is intentionally synchronous — it returns a
 * new `FunnelState` from a pure function. `json-rules-engine` is async (fact
 * loading + caching is one of its features). Bridge:
 *
 *   1. UI calls `await resolveBranching(currentStep, facts)` BEFORE advancing.
 *   2. UI passes `chooseNextStepFromDecision(decision)` to `advance()` so the
 *      sync engine consumes the pre-resolved decision.
 *
 * Three decision shapes:
 *   - `goto`   — navigate to `params.step_id` (rule fired with `goto_step`).
 *   - `stay`   — remain on the current step (rule fired with `stay`).
 *   - `linear` — fall through to the engine's linear next-step calculation.
 *
 * Every shape may carry optional `fieldOverrides` extracted from `set_fields`
 * events. The caller (funnel runtime) writes them into the answer bag before
 * advancing so downstream renderers see the overridden values.
 */
import { evaluateRules, type Rule, type RuleFacts } from "@nextday/rules";
import type { ChooseNextStep, Step } from "@nextday/funnel-engine";

export type BranchingDecision =
  | { kind: "goto"; stepId: string; fieldOverrides?: Readonly<Record<string, unknown>> }
  | { kind: "stay"; fieldOverrides?: Readonly<Record<string, unknown>> }
  | { kind: "linear"; fieldOverrides?: Readonly<Record<string, unknown>> };

/**
 * Session context enrichment forwarded to `evaluateRules` as top-level facts.
 *
 * `application_id` — application slug (e.g. "spicychat") enabling `characterInBrand` rules.
 * `character_id`    — the character loaded with this session (from `?c=` URL param or
 *                     campaign default). Used by character-aware branching rules.
 */
export type BranchingContext = {
  readonly application_id?: string;
  readonly character_id?: string;
};

const isGotoStepEvent = (event: {
  type: string;
  params?: Record<string, unknown>;
}): event is { type: "goto_step"; params: { step_id: string } } =>
  event.type === "goto_step" &&
  typeof event.params?.step_id === "string" &&
  event.params.step_id.length > 0;

const collectFieldOverrides = (
  events: ReadonlyArray<{ type: string; params?: Record<string, unknown> }>,
): Readonly<Record<string, unknown>> | undefined => {
  let merged: Record<string, unknown> | undefined;
  for (const event of events) {
    const fields = event.params?.fields;
    if (fields != null && typeof fields === "object" && !Array.isArray(fields)) {
      merged = { ...(fields as Record<string, unknown>), ...merged };
    }
  }
  return merged;
};

const decideFromEvents = (
  events: ReadonlyArray<{ type: string; params?: Record<string, unknown> }>,
): BranchingDecision => {
  const fieldOverrides = collectFieldOverrides(events);
  for (const event of events) {
    if (isGotoStepEvent(event)) {
      return { kind: "goto", stepId: event.params.step_id, fieldOverrides };
    }
    if (event.type === "stay") return { kind: "stay", fieldOverrides };
  }
  return { kind: "linear", fieldOverrides };
};

/**
 * Run the step's `next_rules` against the supplied fact bag and reduce the
 * result to a `BranchingDecision`. Awaitable; safe to call from any UI
 * handler before invoking the synchronous `advance()`.
 *
 * @param step    - The current step whose `next_rules` will be evaluated.
 * @param facts   - Session fact bag (e.g. `{ answers: {...} }`).
 * @param context - Optional session context enrichment. Fields are merged into
 *                  `facts` as top-level entries (`application_id`, `character_id`)
 *                  so rules using the `characterInBrand` operator can resolve
 *                  brand-aware branching without callers manually constructing
 *                  the merged fact bag.
 */
export const resolveBranching = async (
  step: Step,
  facts: RuleFacts,
  context?: BranchingContext,
): Promise<BranchingDecision> => {
  const rules = (step.next_rules ?? []) as ReadonlyArray<Rule>;
  if (rules.length === 0) return { kind: "linear" };
  const enrichedFacts: RuleFacts = context
    ? {
        ...facts,
        ...(context.application_id !== undefined ? { application_id: context.application_id } : {}),
        ...(context.character_id !== undefined ? { character_id: context.character_id } : {}),
      }
    : facts;
  const result = await evaluateRules(rules, enrichedFacts);
  return decideFromEvents(result.events);
};

/**
 * Build a synchronous `ChooseNextStep` callback from a pre-resolved decision.
 * Pass it through `advance(state, config, { chooseNextStep })` so funnel-engine
 * stays pure-sync.
 *
 *   - `goto`   → returns the target step id.
 *   - `stay`   → returns the current step id (engine pushes a duplicate; the
 *                renderer is expected to short-circuit before submitting).
 *   - `linear` → returns `null` so the engine walks to the linearly next step.
 */
export const chooseNextStepFromDecision =
  (decision: BranchingDecision): ChooseNextStep =>
  ({ current }) => {
    if (decision.kind === "goto") return decision.stepId;
    if (decision.kind === "stay") return current.id;
    return null;
  };
